import { casePath, homePath } from '../core/routes'
import { dictionaries, htmlLang, LANGS, type Lang } from '../i18n'
import { portfolio, type PortfolioItem } from './portfolio'

/**
 * SEO for the work section: per-case meta, hreflang alternates and schema.org JSON-LD.
 * Used by scripts/prerender.mjs (through scripts/ssr-entry.ts) to build /work/<id>/ pages, the
 * Collection schema on the home pages, and sitemap.xml. Everything derives from `portfolio`.
 */

export const ORIGIN = 'https://craft-tower.com'

/** hreflang value per language, as used in the existing <link rel="alternate"> tags. */
export const hreflang: Record<Lang, string> = { en: 'en', me: 'sr-ME', ru: 'ru' }

type JsonLd = Record<string, unknown>

export function absoluteUrl(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${ORIGIN}/${path.replace(/^\/+/, '')}`
}

export const homeUrl = (lang: Lang): string => `${ORIGIN}${homePath(lang)}`
export const caseUrl = (lang: Lang, id: string): string => `${ORIGIN}${casePath(lang, id)}`

function clip(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:.\s]+$/, '')}…`
}

export interface CaseMeta {
  title: string
  description: string
  keywords: string[]
  image: string
}

export function caseMeta(item: PortfolioItem, lang: Lang): CaseMeta {
  const seo = item.seo
  const label = dictionaries[lang].work.links.case
  return {
    title: seo?.title?.[lang] ?? `${item.title[lang]} | ${label} | CRAFT TOWER`,
    description: seo?.description?.[lang] ?? clip((item.summary ?? item.description)[lang], 158),
    keywords: seo?.keywords?.[lang] ?? [...item.tags[lang], item.category[lang]],
    image: absoluteUrl(seo?.image ?? item.cover ?? 'og-image.png'),
  }
}

/** <link rel="alternate" hreflang> targets for one case. */
export function caseAlternates(id: string): { hreflang: string; href: string }[] {
  return [
    ...LANGS.map((lang) => ({ hreflang: hreflang[lang], href: caseUrl(lang, id) })),
    { hreflang: 'x-default', href: caseUrl('en', id) },
  ]
}

/** JSON-LD nodes appended to the site graph on a case page: WebPage, BreadcrumbList, CreativeWork, VideoObject. */
export function caseJsonLd(item: PortfolioItem, lang: Lang): JsonLd[] {
  const d = dictionaries[lang].work
  const meta = caseMeta(item, lang)
  const url = caseUrl(lang, item.id)
  const images = [meta.image, ...(item.gallery ?? []).map((shot) => absoluteUrl(shot.src))]
  const video = item.video?.kind === 'file' ? item.video : null
  const published = item.seo?.published
  const modified = item.seo?.modified ?? published

  const nodes: JsonLd[] = [
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: meta.title,
      description: meta.description,
      inLanguage: htmlLang[lang],
      isPartOf: { '@id': `${ORIGIN}/#website` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      mainEntity: { '@id': `${url}#work` },
      primaryImageOfPage: { '@type': 'ImageObject', url: meta.image },
      ...(modified && { dateModified: modified }),
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'CRAFT TOWER', item: homeUrl(lang) },
        { '@type': 'ListItem', position: 2, name: d.title, item: `${homeUrl(lang)}#work` },
        { '@type': 'ListItem', position: 3, name: item.title[lang], item: url },
      ],
    },
    {
      '@type': 'CreativeWork',
      '@id': `${url}#work`,
      url,
      name: item.title[lang],
      headline: item.title[lang],
      description: item.description[lang],
      abstract: (item.summary ?? item.description)[lang],
      inLanguage: htmlLang[lang],
      genre: item.category[lang],
      keywords: meta.keywords.join(', '),
      image: images,
      creator: { '@id': `${ORIGIN}/#organization` },
      publisher: { '@id': `${ORIGIN}/#organization` },
      mainEntityOfPage: { '@id': `${url}#webpage` },
      ...(published && { datePublished: published }),
      ...(modified && { dateModified: modified }),
      ...(item.year && { copyrightYear: Number(item.year) }),
      ...(item.client && {
        about: {
          '@type': 'Organization',
          name: item.client,
          ...(item.links?.some((l) => l.kind === 'live') && { url: item.links.find((l) => l.kind === 'live')!.url }),
        },
      }),
      ...(item.stack?.length && { tool: item.stack }),
      ...(video && { video: { '@id': `${url}#video` } }),
    },
  ]

  if (video) {
    nodes.push({
      '@type': 'VideoObject',
      '@id': `${url}#video`,
      name: `${item.title[lang]} — ${d.promo}`,
      description: (item.summary ?? item.description)[lang],
      thumbnailUrl: [video.poster ? absoluteUrl(video.poster) : meta.image],
      contentUrl: absoluteUrl(video.src),
      inLanguage: htmlLang[lang],
      ...(video.duration && { duration: video.duration }),
      ...(published && { uploadDate: published }),
    })
  }
  return nodes
}

/** CollectionPage + ItemList for the home page's work section. */
export function collectionJsonLd(lang: Lang): JsonLd {
  const d = dictionaries[lang].work
  const home = homeUrl(lang)
  return {
    '@type': 'CollectionPage',
    '@id': `${home}#work`,
    url: `${home}#work`,
    name: d.title,
    description: d.description,
    inLanguage: htmlLang[lang],
    isPartOf: { '@id': `${ORIGIN}/#website` },
    publisher: { '@id': `${ORIGIN}/#organization` },
    mainEntity: {
      '@type': 'ItemList',
      name: d.title,
      numberOfItems: portfolio.length,
      itemListOrder: 'https://schema.org/ItemListUnordered',
      itemListElement: portfolio.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: caseUrl(lang, item.id),
        name: item.title[lang],
        item: {
          '@type': 'CreativeWork',
          '@id': `${caseUrl(lang, item.id)}#work`,
          url: caseUrl(lang, item.id),
          name: item.title[lang],
          description: (item.summary ?? item.description)[lang],
          genre: item.category[lang],
          inLanguage: htmlLang[lang],
          ...(item.cover && { image: absoluteUrl(item.cover) }),
        },
      })),
    },
  }
}
